// TipTap extension for page-break spacing via ProseMirror decorations.
// Instead of domAtPos (which is unreliable for this use case), we walk
// PM's DOM children directly and map them back to doc positions.

import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { EditorView } from '@tiptap/pm/view';

const SPACER_CLASS = 'paged-editor-break-spacer';

export interface PageBreakOptions {
  contentHeight: number;
  breakZoneHeight: number;
  onPageCount?: (count: number) => void;
}

export const PageBreak = Extension.create<PageBreakOptions>({
  name: 'pageBreak',

  addOptions() {
    return {
      contentHeight: 931,
      breakZoneHeight: 176,
      onPageCount: undefined,
    };
  },

  addProseMirrorPlugins() {
    const opts = this.options;
    let lastPageCount = 1;
    let currentDecos: DecorationSet = DecorationSet.empty;
    let computing = false;

    function computeBreaks(view: EditorView) {
      if (computing) return;
      computing = true;

      try {
        const { doc } = view.state;
        const pmEl = view.dom;
        if (!pmEl || doc.childCount === 0) return;

        // Walk PM DOM children directly — skip spacer decorations
        const domChildren = Array.from(pmEl.children).filter(
          (el) => !el.classList.contains(SPACER_CLASS),
        ) as HTMLElement[];

        // Map DOM positions back to doc positions using view.posAtDOM
        const blocks: { el: HTMLElement; pos: number }[] = [];
        for (const el of domChildren) {
          try {
            const pos = view.posAtDOM(el, 0);
            // posAtDOM returns the position inside the node, we need the position before it
            // For a top-level block, the resolved pos should be at the start of the block
            const resolved = doc.resolve(pos);
            const blockStart = resolved.before(1); // position before the top-level node
            blocks.push({ el, pos: Math.max(0, blockStart) });
          } catch {
            // skip
          }
        }

        // Calculate existing spacer shift
        const existingSpacers = Array.from(
          pmEl.querySelectorAll(`.${SPACER_CLASS}`),
        ) as HTMLElement[];

        const decorations: Decoration[] = [];
        let nextBreakAt = opts.contentHeight;
        let pageCount = 1;

        for (const { el, pos } of blocks) {
          const elTop = el.offsetTop;
          let spacerShift = 0;
          for (const s of existingSpacers) {
            if (s.offsetTop < elTop) spacerShift += s.offsetHeight;
          }
          const pureTop = elTop - spacerShift;
          const pureBottom = pureTop + el.offsetHeight;

          if (pureBottom > nextBreakAt) {
            // Variable spacer height: fill remaining space on current page + break zone
            const remainingOnPage = nextBreakAt - pureTop;
            const spacerH = remainingOnPage + opts.breakZoneHeight;

            decorations.push(
              Decoration.widget(pos, () => {
                const spacer = document.createElement('div');
                spacer.className = SPACER_CLASS;
                spacer.style.height = `${spacerH}px`;
                spacer.style.pointerEvents = 'none';
                spacer.style.userSelect = 'none';
                spacer.contentEditable = 'false';
                return spacer;
              }, { side: -1 }),
            );
            nextBreakAt += opts.contentHeight;
            pageCount++;
            while (pureBottom > nextBreakAt) {
              nextBreakAt += opts.contentHeight;
              pageCount++;
            }
          }
        }

        currentDecos = DecorationSet.create(doc, decorations);

        // Trigger PM to re-read decorations
        const tr = view.state.tr.setMeta('pageBreakUpdate', true);
        tr.setMeta('addToHistory', false);
        view.dispatch(tr);

        if (pageCount !== lastPageCount) {
          lastPageCount = pageCount;
          opts.onPageCount?.(pageCount);
        }
      } finally {
        computing = false;
      }
    }

    const pluginKey = new PluginKey('pageBreak');

    return [
      new Plugin({
        key: pluginKey,
        props: {
          decorations: () => currentDecos,
        },
        view(editorView) {
          let rafId: number | undefined;

          const schedule = () => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => computeBreaks(editorView));
          };

          // Delay initial to let TipTap render
          setTimeout(() => computeBreaks(editorView), 300);

          return {
            update() {
              if (!computing) schedule();
            },
            destroy() {
              if (rafId) cancelAnimationFrame(rafId);
            },
          };
        },
      }),
    ];
  },
});
